import { MeasureData } from "../interfaces";
import {
    buildCompositionSeries,
    CompositionClient,
    CompositionPoint,
} from "./composition";

export interface ProgressSummary {
    start: CompositionPoint;
    now: CompositionPoint;
    /** Negative is a loss. */
    fatKgDelta: number;
    leanKgDelta: number;
    weightKgDelta: number;
    /** 0..1 — the share of weight lost that came from fat mass. */
    changeQuality: number;
    /** 0..1 toward the target body fat, or null when no target is set. */
    progressFraction: number | null;
}

export interface ProgressClient extends CompositionClient {
    target?: { measurements?: { body_fat?: number } };
}

const leanOf = (p: CompositionPoint) => p.leanKg;
const weightOf = (p: CompositionPoint) => p.fatKg + leanOf(p);

/**
 * Change since the first scan, in the framework's terms.
 *
 * Deltas come from the composition series rather than from raw weight, because
 * "5.2kg lost" and "5.4kg of fat lost" are different statements and the coach
 * is being asked to read the second one.
 */
export const buildProgressSummary = (
    measurements: MeasureData[],
    clientData: ProgressClient
): ProgressSummary | null => {
    const { points } = buildCompositionSeries(measurements, clientData);
    if (points.length < 2) return null;

    const start = points[0];
    const now = points[points.length - 1];

    const fatKgDelta = now.fatKg - start.fatKg;
    const leanKgDelta = leanOf(now) - leanOf(start);
    const weightKgDelta = weightOf(now) - weightOf(start);

    // Only meaningful while losing. Fat loss can exceed total loss when lean
    // rose at the same time, which reads as "all of it was fat" — 1, not 1.05.
    const changeQuality =
        weightKgDelta < 0
            ? Math.min(1, Math.max(0, -fatKgDelta / -weightKgDelta))
            : 0;

    const targetBodyFat = clientData.target?.measurements?.body_fat;
    const span =
        targetBodyFat === undefined ? 0 : start.fatPercent - targetBodyFat;
    const progressFraction =
        targetBodyFat === undefined || span <= 0
            ? null
            : Math.min(1, Math.max(0, (start.fatPercent - now.fatPercent) / span));

    return {
        start,
        now,
        fatKgDelta,
        leanKgDelta,
        weightKgDelta,
        changeQuality,
        progressFraction,
    };
};
